import { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
ChartJS.register(ChartDataLabels);

function getAdaptiveBinSize(maxPrice) {
    if (maxPrice === 0) return 1;

    if (maxPrice < 20) {
        return 2;
    }
    
    if (maxPrice <= 100) {
        return 6;
    }

    const magnitude = Math.floor(Math.log10(maxPrice));
    const baseStep = Math.pow(10, magnitude);
    const ratio = maxPrice / baseStep;

    if (ratio < 2) {
        return baseStep / 5;
    } else if (ratio < 5) {
        return baseStep / 2;
    }
    return baseStep;
}

function Histogramchart({ product_prices, filter }) {
    const { t } = useTranslation();
    const [histogramChartData, setHistogramChartData] = useState(null);
    const [histogramChartOptions, setHistogramChartOptions] = useState(null);

    useEffect(() => {
        if (!product_prices) return;

        const maxPrice = Math.max(...product_prices.map(item => item.spu_avg_price));
        const minPrice = Math.min(...product_prices.map(item => item.spu_avg_price));
        const binSize = getAdaptiveBinSize(maxPrice);

        const binStart = Math.floor(minPrice / binSize) * binSize;
        const binEnd = Math.ceil(maxPrice / binSize) * binSize;
        const binCount = Math.ceil((binEnd - binStart) / binSize);

        const bins = Array.from({ length: binCount }, (_, i) => {
            const start = binStart + i * binSize;
            const end = start + binSize;
            return {
                start,
                end,
                mid: (start + end) / 2,
                gmv: 0,
                sales: 0,
                products: 0,
            };
        });

        product_prices.forEach(({ spu_avg_price, total_product_cnt, total_sale_cnt, total_gmv_amt }) => {
            const binIndex = Math.floor((spu_avg_price - binStart) / binSize);
            if (bins[binIndex]) {
                bins[binIndex].products += total_product_cnt;
                bins[binIndex].sales += total_sale_cnt;
                bins[binIndex].gmv += total_gmv_amt;
            }
        });


        setHistogramChartData({
            datasets: [
            {
                label: "GMV",
                data: bins.map(bin => ({
                    x: bin.mid,
                    y: bin.gmv,
                    products: bin.products,
                    sales: bin.sales,
                    gmv: bin.gmv,
                })),
                backgroundColor: "rgba(75, 123, 236, 0.8)",
                barPercentage: 1.0,
                categoryPercentage: 1.0,
            },
            ],
        });

        setHistogramChartOptions({
            responsive: true,
            plugins: {
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        title: (tooltipItems) => {
                            const value = tooltipItems[0].label;

                            const binStart = Math.floor(value / binSize) * binSize;
                            const binEnd = binStart + binSize;

                            return `${binStart} - ${binEnd}`;
                        },
                        label: context => {
                            const data = context.raw;
                            return [
                                `No. of products: ${data.products}`,
                                `Sales: ${data.sales}`,
                                `GMV: ${data.gmv.toLocaleString()}`
                            ];
                        }
                    }
                },
                legend: { display: false },
                datalabels: { display: false },
                title: {
                    display: true,
                    text: `${t("The Distribution of Product Prices")} (${(filter.dateRange || "30") + " days"})`,
                }
            },
            scales: {
                x: {
                    type: "linear",
                    ticks: {
                        stepSize: binSize,
                    },
                    offset: false,
                },
                y: {
                    beginAtZero: true,
                },
            },
        });
    }, [product_prices, filter, t]);

    return (
        <Fragment>
            {histogramChartData && histogramChartOptions && (
                <Bar data={histogramChartData} options={histogramChartOptions} />
            )}
        </Fragment>
    );
}

const mapStateToProps = state => ({
});
const mapDispatchToProps = {
}

export default connect(mapStateToProps, mapDispatchToProps)(Histogramchart);